import {Slider} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNumber} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import {EagFilterProps} from '../../types/EagFilterProps';
import {calculateSliderTicks} from '../../utils/calculateSliderTicks';
import {formatSliderValue} from '../../utils/formatSliderValue';
import {getQuickFilterFromString} from '../../utils/quickFilterValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';

type SliderFilterProps = EagFilterProps & {
  min: number;
  max: number;
  minOpen: boolean;
  maxOpen: boolean;
  unit: string | null;
  stepSize: number | null;
  steps: number[];
  markerSize: number | null;
  markers?: number[];
  decimalsAllowed: boolean;
  inputsVisible: boolean;
  quickFilters?: Array<{value: string; label: string}>;
};

export function SliderFilter(props: SliderFilterProps) {
  const ticks = calculateSliderTicks(props.min, props.max, props.markerSize, props.markers);
  const midValue = (props.max + props.min) / 2;

  const model = props.value[FILTER_VALUE_KEY] as number | string | null;

  const onModelChange = (newValue: number | string | null) => {
    props.onChange({[FILTER_VALUE_KEY]: newValue});
  };

  const isQuickFilterValue = getQuickFilterFromString(model);

  // Thumb position derives from the applied model (midpoint when no numeric value
  // is applied). No local thumb state: reopening the filter, preset switches and
  // clear-all therefore always show the applied value.
  const sliderValue = isNumber(model) ? model : midValue;

  const onSliderValueChange = (value: number) => {
    onModelChange(value);
  };

  return (
    <VStack spacing={2}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={isQuickFilterValue}
        defaultValue={null}
        setFilterValue={onModelChange}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('slider-filter-quickFilters', props)}
      />
      <Slider
        isDisabled={props.isDisabled}
        min={props.min}
        max={props.max}
        step={props.stepSize ?? (props.steps?.length ? props.steps : 1)}
        value={sliderValue}
        onChange={onSliderValueChange}
        formatValue={(value) => formatSliderValue(value, props.unit)}
        ticks={ticks}
        onlyEdges={true}
        displayValue="auto"
        data-testid={suffixTestId('slider-filter', props)}
      />
    </VStack>
  );
}
